/**
 * 색상 유틸리티 함수
 * 제한된 RGB 구간에서 랜덤하게 색상을 생성
 */

/**
 * 그레이톤 색상 생성 (흰색 ~ 어두운 회색)
 * @param {number} minLightness - 최소 밝기 (0-100)
 * @param {number} maxLightness - 최대 밝기 (0-100)
 * @returns {string} RGB 색상 문자열
 */
export function generateGrayColor(minLightness = 200, maxLightness = 100) {
    const lightness = Math.floor(Math.random() * (minLightness - maxLightness + 1)) + maxLightness;
    return `rgb(${lightness}, ${lightness}, ${lightness})`;
}

/**
 * 제한된 RGB 구간에서 랜덤 색상 생성
 * @param {Object} options - 색상 생성 옵션
 * @param {number} options.minR - 최소 R 값 (0-255)
 * @param {number} options.maxR - 최대 R 값 (0-255)
 * @param {number} options.minG - 최소 G 값 (0-255)
 * @param {number} options.maxG - 최대 G 값 (0-255)
 * @param {number} options.minB - 최소 B 값 (0-255)
 * @param {number} options.maxB - 최대 B 값 (0-255)
 * @returns {string} RGB 색상 문자열
 */
export function generateRandomColorInRange(options = {}) {
    const { minR = 200, maxR = 100, minG = 200, maxG = 100, minB = 200, maxB = 100 } = options;

    const r = Math.floor(Math.random() * (maxR - minR + 1)) + minR;
    const g = Math.floor(Math.random() * (maxG - minG + 1)) + minG;
    const b = Math.floor(Math.random() * (maxB - minB + 1)) + minB;

    return `rgb(${r}, ${g}, ${b})`;
}

/**
 * 그레이톤 색상 배열 생성 (트리맵, 차트용)
 * @param {number} count - 생성할 색상 개수
 * @returns {Array<string>} 색상 배열
 */
export function generateGrayColorPalette(count) {
    const colors = [];
    const minLightness = 240; // 매우 밝은 회색
    const maxLightness = 120; // 어두운 회색

    for (let i = 0; i < count; i++) {
        const lightness = Math.floor(minLightness - ((minLightness - maxLightness) * i) / Math.max(1, count - 1));
        colors.push(`rgb(${lightness}, ${lightness}, ${lightness})`);
    }

    return colors;
}

/**
 * 제한된 구간에서 랜덤 색상 배열 생성
 * @param {number} count - 생성할 색상 개수
 * @param {Object} options - 색상 생성 옵션
 * @returns {Array<string>} 색상 배열
 */
export function generateRandomColorPalette(count, options = {}) {
    const colors = [];
    const usedColors = new Set();

    for (let i = 0; i < count; i++) {
        let color;
        let attempts = 0;

        // 중복 방지를 위해 최대 100번 시도
        do {
            color = generateRandomColorInRange(options);
            attempts++;
        } while (usedColors.has(color) && attempts < 100);

        usedColors.add(color);
        colors.push(color);
    }

    return colors;
}

/**
 * 핵심 강조용 푸른색 (제한적으로 사용)
 * @returns {string} 푸른색 RGB 문자열
 */
export function getPrimaryBlueColor() {
    return "rgb(59, 130, 246)"; // #3b82f6
}

/**
 * 어두운 푸른색 (제한적으로 사용)
 * @returns {string} 어두운 푸른색 RGB 문자열
 */
export function getDarkBlueColor() {
    return "rgb(30, 64, 175)"; // #1e40af
}

/**
 * 푸른색 톤 색상 배열 생성 (트리맵, 차트용)
 * 밝은 푸른색부터 어두운 푸른색까지 그라데이션 생성
 * @param {number} count - 생성할 색상 개수
 * @returns {Array<string>} 푸른색 톤 색상 배열
 */
export function generateBlueColorPalette(count) {
    const colors = [];

    // 푸른색 범위를 #3b82f6 (rgb(59, 130, 246)) 기준으로 설정
    // 밝은 푸른색부터 #3b82f6 근처까지 그라데이션
    const startR = 120; // 밝은 푸른색 시작 R
    const startG = 170; // 밝은 푸른색 시작 G
    const startB = 250; // 밝은 푸른색 시작 B

    const endR = 59; // #3b82f6 근처 끝 R
    const endG = 130; // #3b82f6 근처 끝 G
    const endB = 246; // #3b82f6 근처 끝 B

    for (let i = 0; i < count; i++) {
        // 0부터 1까지의 진행률 계산
        const linearProgress = count > 1 ? i / (count - 1) : 0;

        // 적절한 가중치를 적용하여 색상 간 차이를 자연스럽게
        // progress^1.8을 사용하여 초반에는 느리게, 후반에는 빠르게 변화
        // 이를 통해 각 색상 간 구분감을 적절하게 유지
        const weightedProgress = Math.pow(linearProgress, 1.8);

        // 가중치가 적용된 보간으로 RGB 값 계산
        const r = Math.floor(startR - (startR - endR) * weightedProgress);
        const g = Math.floor(startG - (startG - endG) * weightedProgress);
        const b = Math.floor(startB - (startB - endB) * weightedProgress);

        colors.push(`rgb(${r}, ${g}, ${b})`);
    }

    return colors;
}

/**
 * 베이스 컬러의 밝기에 따라 대비되는 텍스트 색상 반환
 * 배경색이 짙을수록(어두울수록) 흰색에 가까워지고,
 * 배경색이 옅을수록(밝을수록) 짙은 그레이색에 가까워짐
 * @param {string} baseColor - RGB 색상 문자열 (예: "rgb(59, 130, 246)" 또는 "#3b82f6")
 * @returns {string} 흰색 또는 짙은 그레이색 사이의 RGB 색상 문자열
 */
export function getContrastTextColor(baseColor) {
    if (!baseColor) {
        return "rgb(64, 64, 64)"; // 기본값: 짙은 그레이
    }

    // RGB 값 추출
    let r, g, b;

    // rgb(r, g, b) 형식 파싱
    const rgbMatch = baseColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
        r = parseInt(rgbMatch[1], 10);
        g = parseInt(rgbMatch[2], 10);
        b = parseInt(rgbMatch[3], 10);
    }
    // #RRGGBB 또는 #RGB 형식 파싱
    else if (baseColor.startsWith("#")) {
        const hex = baseColor.replace("#", "");
        if (hex.length === 3) {
            // #RGB -> #RRGGBB 변환
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length === 6) {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        } else {
            return "rgb(64, 64, 64)"; // 잘못된 형식
        }
    } else {
        return "rgb(64, 64, 64)"; // 지원하지 않는 형식
    }

    // 배경색의 밝기 계산 (상대 휘도 사용)
    const normalize = (value) => {
        value = value / 255;
        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    };

    const rNorm = normalize(r);
    const gNorm = normalize(g);
    const bNorm = normalize(b);

    // 상대 휘도 계산 (0~1 범위)
    const luminance = 0.2126 * rNorm + 0.7152 * gNorm + 0.0722 * bNorm;

    // 목표 색상 정의
    const whiteR = 255; // 흰색
    const whiteG = 255;
    const whiteB = 255;

    const darkGrayR = 64; // 짙은 그레이색
    const darkGrayG = 64;
    const darkGrayB = 64;

    // 밝기가 낮을수록(어두울수록) 흰색에 가까워지도록 보간
    // luminance가 0에 가까우면(어두우면) 흰색, 1에 가까우면(밝으면) 짙은 그레이
    // threshold와 강한 곡선을 사용하여 어두운 색일 때 더 빠르게 흰색에 가까워지도록 조정
    const luminanceThreshold = 0.5; // 이 밝기 이하는 더 강하게 흰색으로 전환
    const whiteTransitionExponent = 0.1; // 지수값: 낮을수록 어두운 색일 때 더 빠르게 흰색으로 전환

    let whiteRatio;
    if (luminance <= luminanceThreshold) {
        // 어두운 색일 때: 더 강한 곡선 사용 + threshold 보정
        const normalizedDarkness = luminance / luminanceThreshold; // 0~1 범위로 정규화
        const invertedDarkness = 1 - normalizedDarkness;
        // 매우 강한 곡선으로 흰색에 가까워지도록
        whiteRatio = Math.pow(invertedDarkness, whiteTransitionExponent);
        // threshold 보정: 어두운 색일 때 최소한의 흰색 비율 보장
        whiteRatio = Math.max(whiteRatio, 1); // 최소 100% 흰색
    } else {
        // 밝은 색일 때: 선형 보간
        const normalizedBrightness = (luminance - luminanceThreshold) / (1 - luminanceThreshold);
        whiteRatio = 1 - normalizedBrightness; // 밝을수록 흰색 비율 감소
    }

    const grayRatio = 1 - whiteRatio; // 밝을수록 1에 가까워짐 (짙은 그레이)

    // 흰색과 짙은 그레이 사이를 보간
    const textR = Math.round(whiteR * whiteRatio + darkGrayR * grayRatio);
    const textG = Math.round(whiteG * whiteRatio + darkGrayG * grayRatio);
    const textB = Math.round(whiteB * whiteRatio + darkGrayB * grayRatio);

    // RGB 값은 0-255 범위로 제한
    const finalR = Math.min(255, Math.max(0, textR));
    const finalG = Math.min(255, Math.max(0, textG));
    const finalB = Math.min(255, Math.max(0, textB));

    return `rgb(${finalR}, ${finalG}, ${finalB})`;
}
